import { useState } from 'react';
import { exchanges, verifyCredentials } from '@/api/endpoints';
import type { ControlRequestRow, Exchange, ExchangeAccount } from '@/api/types';
import { RequirePermission } from '@/components/RequirePermission';
import { waitForControlRequest } from '@/lib/controlRequests';
import { describe } from '@/lib/errors';

type KeyForm = { key: string; secret: string; passphrase: string };

/** What the exchange said about a key, in plain words. */
export function keyVerdict(permissions: Record<string, unknown> | null | undefined): string {
  if (!permissions) return 'not checked with the exchange yet';
  if (permissions.valid === false) {
    return `rejected by the exchange: ${String(permissions.error ?? 'no reason given')}`;
  }
  const trade = permissions.can_trade ? 'can trade' : 'cannot trade';
  const withdraw =
    permissions.withdrawal_provable === false
      ? 'withdrawal permission cannot be proven on this exchange'
      : permissions.can_withdraw
        ? 'CAN WITHDRAW: remove that permission before going live'
        : 'cannot withdraw';
  return `valid, ${trade}, ${withdraw}`;
}

/**
 * Testnet and live accounts: create them, give them an API key, and check the
 * key with the exchange.
 *
 * Storing a key asks for your password again, and the exchange is asked
 * about it straight away. The answer is also what the live activation gate
 * reads, so a key never checked cannot go live.
 */
export function ConnectionsPanel({
  exchange,
  accounts,
  onChanged,
}: {
  exchange: Exchange;
  accounts: ExchangeAccount[];
  onChanged: () => void;
}) {
  const [label, setLabel] = useState('');
  const [mode, setMode] = useState<'TESTNET' | 'LIVE' | 'PAPER'>('TESTNET');
  const [keys, setKeys] = useState<Record<number, KeyForm>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const connected = accounts.filter((account) => account.mode !== 'PAPER');

  async function run(tag: string, work: () => Promise<void>) {
    setBusy(tag);
    setError(null);
    setNotice(null);
    try {
      await work();
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(null);
      onChanged();
    }
  }

  function report(account: ExchangeAccount, request: ControlRequestRow) {
    if (request.state === 'PENDING') {
      setNotice(`${account.label}: the check is still queued; its result will appear here.`);
    } else if (request.state === 'FAILED') {
      setError(`${account.label}: ${String(request.result?.error ?? 'the check failed')}`);
    } else {
      setNotice(`${account.label}: key ${keyVerdict(request.result)}.`);
    }
  }

  const create = () =>
    run('create', async () => {
      await exchanges.createAccount({ exchange_code: exchange.code, label: label.trim(), mode });
      setLabel('');
      setNotice(`${mode.toLowerCase()} account created. Store its API key below.`);
    });

  const storeKey = (account: ExchangeAccount) =>
    run(`key-${account.id}`, async () => {
      const form = keys[account.id] ?? { key: '', secret: '', passphrase: '' };
      const stored = (await exchanges.putCredentials(account.id, {
        api_key: form.key.trim(),
        api_secret: form.secret.trim(),
        ...(exchange.requires_passphrase ? { passphrase: form.passphrase } : {}),
      })) as { verification_request_id?: number };
      setKeys((current) => ({ ...current, [account.id]: { key: '', secret: '', passphrase: '' } }));
      if (stored.verification_request_id) {
        setNotice(`${account.label}: key stored. Checking it with ${exchange.display_name}…`);
        report(account, await waitForControlRequest(stored.verification_request_id));
      }
    });

  const verify = (account: ExchangeAccount) =>
    run(`verify-${account.id}`, async () => {
      const queued = await verifyCredentials(account.id);
      setNotice(`${account.label}: checking the key with ${exchange.display_name}…`);
      report(account, await waitForControlRequest(queued.id));
    });

  const setField = (id: number, field: keyof KeyForm, value: string) =>
    setKeys((current) => ({
      ...current,
      [id]: { ...(current[id] ?? { key: '', secret: '', passphrase: '' }), [field]: value },
    }));

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>Connections</h2>
        <span className="hint">
          {exchange.code === 'kucoin'
            ? 'No testnet on this exchange: live only'
            : exchange.code === 'bitget'
              ? 'Testnet is Bitget demo trading'
              : 'Testnet and live'}
        </span>
      </div>
      <div className="panel-body" style={{ display: 'grid', gap: 'var(--space-4)' }}>
        {error ? <div className="notice notice--error">{error}</div> : null}
        {notice ? <div className="notice">{notice}</div> : null}

        <RequirePermission permission="config:exchange_accounts">
          <div className="assign">
            <label>
              <span>New account</span>
              <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="label" />
            </label>
            <label>
              <span>Mode</span>
              <select value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
                <option value="TESTNET">testnet</option>
                <option value="LIVE">live</option>
                <option value="PAPER">paper</option>
              </select>
            </label>
            <button type="button" disabled={!label.trim() || busy !== null} onClick={() => void create()}>
              Add account
            </button>
          </div>
        </RequirePermission>

        {connected.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)' }}>
            No testnet or live account on {exchange.display_name} yet.
          </p>
        ) : (
          connected.map((account) => {
            const form = keys[account.id] ?? { key: '', secret: '', passphrase: '' };
            const complete =
              form.key.trim() && form.secret.trim() && (!exchange.requires_passphrase || form.passphrase);
            return (
              <div key={account.id} data-testid={`connection-${account.id}`} style={{ display: 'grid', gap: 'var(--space-2)' }}>
                <strong>
                  {account.label}{' '}
                  <span className="muted">
                    ({account.practice_environment === 'DEMO' ? 'demo trading' : account.mode.toLowerCase()})
                  </span>
                </strong>
                <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
                  {account.credentials
                    ? `Key ${account.credentials.api_key_masked}: ${keyVerdict(account.credentials.permissions)}`
                    : 'No API key stored.'}
                </span>
                <RequirePermission permission="danger:credentials">
                  <div className="assign">
                    <label>
                      <span>API key</span>
                      <input value={form.key} onChange={(e) => setField(account.id, 'key', e.target.value)} autoComplete="off" />
                    </label>
                    <label>
                      <span>API secret</span>
                      <input type="password" value={form.secret} onChange={(e) => setField(account.id, 'secret', e.target.value)} autoComplete="new-password" />
                    </label>
                    {exchange.requires_passphrase ? (
                      <label>
                        <span>Passphrase</span>
                        <input type="password" value={form.passphrase} onChange={(e) => setField(account.id, 'passphrase', e.target.value)} autoComplete="new-password" />
                      </label>
                    ) : null}
                    <button type="button" disabled={!complete || busy !== null} onClick={() => void storeKey(account)}>
                      {account.credentials ? 'Replace key' : 'Store key'}
                    </button>
                  </div>
                </RequirePermission>
                {account.credentials ? (
                  <RequirePermission permission="config:exchange_accounts">
                    <div>
                      <button type="button" disabled={busy !== null} onClick={() => void verify(account)}>
                        {busy === `verify-${account.id}` ? 'Checking…' : 'Verify key'}
                      </button>
                    </div>
                  </RequirePermission>
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}
